import React from 'react'
import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import Card from './Card'
import Txt from './Txt'
import IconButton from './IconButton'
import SimpleTable, { SimpleTableObject } from './SimpleTable'
import MenuView from './MenuView'
import SourceMenuPicker from './SourceMenuPicker'
import { useMenuByVersion } from '../queries/menuByVersion'
import { useMenuActions } from '../utils/useMenuActions'
import { Restaurant } from '../utils/restaurant'

const sxCard = { p: '1rem', flexDirection: 'column' } as const
const sxHeader = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	mb: '0.75rem',
} as const
const sxColumn = { display: 'flex', flexDirection: 'column', gap: '1.5rem' } as const
const sxActions = { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.35rem' } as const
const sxSelect = { minWidth: '11rem' } as const

const dict = {
	version: 'Version',
	state: 'Status',
	source: 'Speisekarte',
	date: 'Datum',
}

const inactiveKeys = ['id']

type MenuHistoryItem = NonNullable<Restaurant['menuHistory']>[number]

type MenuStateOption = 'IMPORTED' | 'ACTIVE_PREVIEW' | 'ACTIVE' | 'OUTDATED' | 'DELETE'

const menuStateLabels: Record<MenuStateOption, string> = {
	IMPORTED: 'Importiert',
	ACTIVE_PREVIEW: 'Aktiv (Preview)',
	ACTIVE: 'Aktiv',
	OUTDATED: 'Veraltet',
	DELETE: 'Löschen',
}

const formatDate = (iso: string): string => {
	const d = new Date(iso)
	return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', {
		hour: '2-digit',
		minute: '2-digit',
	})}`
}

// Zur Wahl steht der aktuelle Status und jeder, in den die Version von dort wechseln darf. Aktivieren
// lässt sich, solange keine Version aktiv ist: ein Import oder die neueste veraltete Version.
const MenuStateSelect = React.memo(
	({
		item,
		isLatest,
		hasActiveVersion,
		disabled,
		onSelect,
	}: {
		item: MenuHistoryItem
		isLatest: boolean
		hasActiveVersion: boolean
		disabled: boolean
		onSelect: (item: MenuHistoryItem, target: MenuStateOption) => void
	}): JSX.Element => {
		const current = (
			item.state === 'ACTIVE' && item.preview ? 'ACTIVE_PREVIEW' : item.state
		) as MenuStateOption

		const options = React.useMemo<MenuStateOption[]>(() => {
			const isActivatable =
				!hasActiveVersion &&
				(item.state === 'IMPORTED' || (item.state === 'OUTDATED' && isLatest))
			return [
				current,
				...(isActivatable ? (['ACTIVE_PREVIEW', 'ACTIVE'] as const) : []),
				...(current === 'ACTIVE_PREVIEW' ? (['ACTIVE'] as const) : []),
				...(item.state === 'ACTIVE' ? (['OUTDATED'] as const) : []),
				...(item.state === 'IMPORTED' && isLatest ? (['DELETE'] as const) : []),
			]
		}, [current, hasActiveVersion, isLatest, item.state])

		const handleChange = React.useCallback(
			(event: SelectChangeEvent) => {
				onSelect(item, event.target.value as MenuStateOption)
			},
			[item, onSelect]
		)

		return (
			<Select
				size="small"
				value={current}
				onChange={handleChange}
				disabled={disabled || options.length === 1}
				sx={sxSelect}
			>
				{options.map((option) => (
					<MenuItem key={option} value={option}>
						{menuStateLabels[option]}
					</MenuItem>
				))}
			</Select>
		)
	}
)

const MenuHistory = (): JSX.Element => {
	const {
		selectedRestaurantId,
		menuHistory,
		latestMenu,
		isRoot,
		draftLoading,
		deleteImportLoading,
		handleDeleteImportedMenu,
		handleActivate,
		handleActivateAsPreview,
		handleGoLive,
		handleDeactivateMenu,
	} = useMenuActions()

	const [selectedMenuVersion, setSelectedMenuVersion] = React.useState<number | null>(null)
	const [showMenu, setShowMenu] = React.useState(false)

	const { loading: versionedLoading } = useMenuByVersion({
		restaurantId: selectedMenuVersion !== null ? selectedRestaurantId : null,
		menuVersion: selectedMenuVersion,
	})

	const hasActiveVersion = menuHistory?.some((item) => item.state === 'ACTIVE') ?? false

	const objects = React.useMemo<SimpleTableObject[]>(
		() =>
			menuHistory?.map((e) => ({
				id: String(e.version),
				__type: 'menuLog',
				version: `v${e.version}`,
				state: e.preview ? `${e.state} (Preview)` : e.state,
				source: e.sourcePdf ?? '–',
				date: formatDate(e.date),
			})) ?? [],
		[menuHistory]
	)

	const handleCloseView = React.useCallback(() => {
		setShowMenu(false)
	}, [])

	// Die Version wird erst geladen; das Menü erscheint, sobald ihre Daten da sind.
	const handleView = React.useCallback((id: unknown) => {
		setSelectedMenuVersion(Number(id))
		setShowMenu(true)
	}, [])

	// Jeder Wechsel läuft über den Bestätigungsdialog der jeweiligen Aktion.
	const handleSelectState = React.useCallback(
		(item: MenuHistoryItem, target: MenuStateOption) => {
			if (target === 'ACTIVE_PREVIEW') {
				handleActivateAsPreview(item.version, item.state === 'IMPORTED')
			} else if (target === 'ACTIVE') {
				if (item.state === 'ACTIVE') {
					handleGoLive(item.version)
				} else {
					handleActivate(item.version, item.state === 'IMPORTED')
				}
			} else if (target === 'OUTDATED') {
				handleDeactivateMenu(item.version)
			} else if (target === 'DELETE') {
				handleDeleteImportedMenu()
			}
		},
		[
			handleActivateAsPreview,
			handleGoLive,
			handleActivate,
			handleDeactivateMenu,
			handleDeleteImportedMenu,
		]
	)

	const renderActions = React.useCallback(
		(id: string) => {
			const rowItem = menuHistory?.find((item) => String(item.version) === id)
			return rowItem ? (
				<Box sx={sxActions}>
					{isRoot ? (
						<MenuStateSelect
							item={rowItem}
							isLatest={rowItem.version === latestMenu?.version}
							hasActiveVersion={hasActiveVersion}
							disabled={draftLoading || deleteImportLoading}
							onSelect={handleSelectState}
						/>
					) : null}
					<IconButton
						id={id}
						iconName="Visibility"
						size="small"
						toolTip="Menü ansehen"
						onClick={handleView}
						loading={selectedMenuVersion === rowItem.version && versionedLoading}
					/>
				</Box>
			) : (
				<></>
			)
		},
		[
			menuHistory,
			latestMenu,
			hasActiveVersion,
			isRoot,
			draftLoading,
			deleteImportLoading,
			handleSelectState,
			handleView,
			selectedMenuVersion,
			versionedLoading,
		]
	)

	return showMenu && !versionedLoading ? (
		<MenuView
			restaurantId={selectedRestaurantId ?? undefined}
			menuVersion={selectedMenuVersion}
			onClose={handleCloseView}
		/>
	) : (
		<Box sx={sxColumn}>
			<Card sx={sxCard}>
				<Box sx={sxHeader}>
					<Txt variant="h6" fontWeight="bold">
						Menü-Historie
					</Txt>
					{isRoot ? <SourceMenuPicker /> : null}
				</Box>
				<SimpleTable
					objects={objects}
					dict={dict}
					inactiveKeys={inactiveKeys}
					disableExpandIcons
					renderActions={renderActions}
				/>
			</Card>
		</Box>
	)
}

export default React.memo(MenuHistory)
